import PropTypes from 'prop-types';
import React from 'react';

/**
 * Streaming indicator dots animation
 */
const StreamingIndicator = () => (
  <div className="flex items-center ml-2">
    <div className="flex space-x-1">
      <div
        className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"
        style={{ animationDelay: '0ms', animationDuration: '1s' }}
      />
      <div
        className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"
        style={{ animationDelay: '200ms', animationDuration: '1s' }}
      />
      <div
        className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"
        style={{ animationDelay: '400ms', animationDuration: '1s' }}
      />
    </div>
    <span className="text-xs text-blue-600 ml-2">responding...</span>
  </div>
);

/**
 * User message bubble
 */
export const UserMessage = ({ content }) => {
  return (
    <div className="p-4 rounded-lg shadow-sm bg-gray-50 border border-gray-100 mr-4">
      <div className="flex items-center gap-2 mb-2">
        <p className="font-medium text-sm text-gray-700">You</p>
      </div>
      <p className="text-gray-600">{content}</p>
    </div>
  );
};

UserMessage.propTypes = {
  content: PropTypes.string.isRequired,
};

/**
 * Assistant message bubble with streaming support
 */
export const AssistantMessage = ({ message, speechEnabled }) => {
  // Remove "END HERE" from the displayed message
  const displayContent = message.content?.replace('END HERE', '') || '';

  return (
    <div className="p-4 rounded-lg shadow-sm bg-blue-50 border border-blue-100 ml-4">
      <div className="flex items-center gap-2 mb-2">
        <img src="/writingcar.png" alt="InternHub Cat" className="w-6 h-6 rounded-[20%]" />
        <p className="font-medium text-sm text-gray-700">InternHub Cat</p>
        {message.isStreaming && <StreamingIndicator />}
      </div>
      <div className="text-gray-600">
        {/* Non-audio mode: simple character-by-character reveal */}
        {!speechEnabled && message.fullText ? (
          <div className="leading-relaxed">{displayContent}</div>
        ) : speechEnabled && message.sentences && message.sentences.length > 0 ? (
          /* Audio mode: complex sentence structure with character animation */
          <div className="leading-relaxed">
            {message.sentences.map((sentence, sentenceIndex) => {
              const isVisible = sentenceIndex <= (message.currentDisplayIndex ?? -1);

              return (
                <span
                  key={sentenceIndex}
                  className={`inline transition-opacity duration-75 ease-out ${
                    isVisible ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {/* Check if sentence has progressive character structure */}
                  {sentence.characters && sentence.characters.length > 0 ? (
                    <>
                      {/* Progressive character reveal */}
                      {sentence.characters.map((charObj, charIndex) => (
                        <span
                          key={charIndex}
                          className={`inline transition-opacity duration-75 ease-out ${
                            charObj.isVisible ? 'opacity-100' : 'opacity-0'
                          }`}
                        >
                          {charObj.char}
                        </span>
                      ))}
                    </>
                  ) : (
                    // Fallback to sentence-level display
                    sentence.text
                  )}
                  {/* Add space between sentences */}
                  {sentenceIndex < message.sentences.length - 1 && ' '}
                </span>
              );
            })}
          </div>
        ) : (
          <>
            {/* Fallback for messages without special structure */}
            {displayContent && (
              <span className="whitespace-pre-wrap leading-relaxed">{displayContent}</span>
            )}
            {message.isStreaming && !displayContent && (
              <span className="text-gray-400 italic">Preparing response...</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

AssistantMessage.propTypes = {
  message: PropTypes.shape({
    content: PropTypes.string,
    fullText: PropTypes.string,
    isStreaming: PropTypes.bool,
    sentences: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        characters: PropTypes.arrayOf(
          PropTypes.shape({
            char: PropTypes.string,
            isVisible: PropTypes.bool,
          })
        ),
      })
    ),
    currentDisplayIndex: PropTypes.number,
  }).isRequired,
  speechEnabled: PropTypes.bool.isRequired,
};

/**
 * Live transcript bubble (shown while user is speaking)
 */
export const TranscriptBubble = ({ transcript }) => {
  return (
    <div className="p-4 rounded-lg shadow-sm bg-green-50 border border-green-100 mr-4 animate-pulse">
      <p className="font-medium text-sm mb-1 text-gray-700">You (speaking):</p>
      <p className="text-gray-600">{transcript}</p>
    </div>
  );
};

TranscriptBubble.propTypes = {
  transcript: PropTypes.string.isRequired,
};
